import { useEffect, useState } from "react";

import type { StoffEingabe } from "@repo/objektkalkulation-calc";

import { Blatt } from "./Blatt";

export type StoffVorlage = {
  id: string;
  herstellerName: string;
  name: string;
  nummer: string;
  farbe: string | null;
  kollektion: string | null;
  art: string;
  ballenbreiteCm: number;
  ekProMeter: number;
  faktor: number;
  stoff: { bezeichnung: string; ekProMeter: number; margeProzent: number; bahnbreiteCm: number };
};

const BASIS = import.meta.env.VITE_KALK_URL ?? "/kalk";

/** Stoff aus der Stoffliste der Konfigurator-API übernehmen – mit EK, Faktor und Ballenbreite. */
export function StoffAuswahl({ offen, onClose, onWahl, hinweis }: { offen: boolean; onClose: () => void; onWahl: (s: StoffEingabe, vorlage: StoffVorlage) => void; hinweis?: string }) {
  const [suche, setSuche] = useState("");
  const [liste, setListe] = useState<StoffVorlage[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    if (!offen) return;
    const ac = new AbortController();
    setLaedt(true);
    fetch(`${BASIS}/api/stoffe?limit=60${suche ? `&suche=${encodeURIComponent(suche)}` : ""}`, { signal: ac.signal })
      .then(async (res) => {
        if (res.status === 503) throw new Error("Stoffliste nicht verbunden: DATABASE_URL der Kalkulations-API fehlt.");
        if (!res.ok) throw new Error(`Stoffliste nicht ladbar (${res.status})`);
        setListe((await res.json()) as StoffVorlage[]);
        setFehler(null);
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        setFehler(err instanceof Error ? err.message : "Stoffliste nicht ladbar");
      })
      .finally(() => !ac.signal.aborted && setLaedt(false));
    return () => ac.abort();
  }, [offen, suche]);

  return (
    <Blatt offen={offen} onClose={onClose} titel="Stoff aus der Stoffliste">
      <div className="kalk-auswahl">
        <input
          type="search"
          className="kalk-auswahl__suche"
          placeholder="Name, Nummer, Farbe oder Hersteller …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          autoFocus
        />
        {hinweis && <p className="muted small">{hinweis}</p>}
        {fehler && <p className="kalk-fehler">{fehler}</p>}
        <ul className="kalk-auswahl__liste" aria-busy={laedt}>
          {liste.map((v) => (
            <li key={v.id}>
              <button type="button" className="kalk-auswahl__eintrag" onClick={() => { onWahl(v.stoff, v); onClose(); }}>
                <span className="kalk-auswahl__titel">
                  {v.name}
                  {v.nummer && <small> {v.nummer}</small>}
                  {v.farbe && <small> · {v.farbe}</small>}
                </span>
                <span className="kalk-auswahl__meta">{v.herstellerName}{v.kollektion ? ` · ${v.kollektion}` : ""} · {v.art}</span>
                <span className="kalk-auswahl__zahlen">
                  <span>{v.ballenbreiteCm} cm</span>
                  <span>{v.ekProMeter.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}/m</span>
                  <span>× {v.faktor.toLocaleString("de-DE")}</span>
                </span>
              </button>
            </li>
          ))}
          {!laedt && !fehler && liste.length === 0 && <li className="muted small">Keine Stoffe gefunden.</li>}
        </ul>
      </div>
    </Blatt>
  );
}
